import { Controller } from "stimulus";
import consumer from "channels/consumer";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";
import { countdown } from "utils/live_poll";

// Matches only http(s):// URLs (bare domains and javascript:/data:/mailto: never match). XSS-safety
// comes from the DOM build path (createElement + setAttribute + textContent, never innerHTML) and the
// forced https?:// scheme; the "<" exclusion is defense-in-depth. Flags are applied per use in fillBody.
const CHAT_URL_REGEX = /https?:\/\/[^\s<]+/;

// The counter only appears once a message is this close (or closer) to the input's maxlength,
// so it stays out of the way for the vast majority of short messages.
const COUNTER_VISIBLE_REMAINING = 50;

// A fast double-click on the trigger icon lands its second click on the backdrop the dialog just
// opened over (same screen position), so a backdrop dismiss within this window of opening is ignored.
const CONFIRM_DISMISS_GRACE_MS = 250;

// Must beat Action Cable's own 6s ConnectionMonitor stale threshold so a half-open socket is caught here first.
const SEND_ACK_TIMEOUT_MS = 2000;

// Generous enough to cover reopen()'s own ~500ms reopenDelay plus real handshake/resubscribe time -
// this is "how long to wait for the reconnect to happen at all," not "how long to wait for an ack,"
// which resendPendingMessages() re-times at the tight SEND_ACK_TIMEOUT_MS once actually resent.
const RECONNECT_TIMEOUT_MS = 5000;

// Mirrors ChatContext::TIMEOUT_DURATIONS (app/services/chat_context.rb) exactly - the server-side
// allowlist for timeout_user's `seconds` param. Any other value is a silent no-op server-side, so
// these three duration chips ARE the client-side validation; there is no free-text duration input.
const TIMEOUT_DURATIONS = [60, 600, 3600];
const TIMEOUT_DEFAULT_SECONDS = 600;

// The confirm dialog's Tab trap re-queries this on every Tab press rather than caching a stop
// list, so a variant switch (or the timeout variant's own chips/textarea) is picked up live.
const CONFIRM_FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default class ChatPanel extends Controller {
  static targets = [
    "messages", "input", "submit", "closed", "timedOut", "timedOutCountdown", "disconnected", "hint",
    "sendFailedNotice", "counter", "deleteIcon", "banIcon", "timeoutIcon", "ellipsisIcon",
    "moderatorBadge", "announcement", "announcementBody", "announcementText", "announcementToggle",
    "announcementPlaceholder", "messageTemplate", "pendingMessageTemplate", "tipMessageTemplate",
    "privateMessageTemplate", "moderatorActionsTemplate", "tipModeratorActionsTemplate", "confirm",
    "confirmCancel", "confirmAccept", "confirmPreview", "confirmPreviewAvatar", "confirmPreviewAvatarFallback",
    "confirmPreviewName", "confirmPreviewAmount", "confirmPreviewBody", "confirmTitleName",
    "timeoutDurationChip", "timeoutReason",
  ];
  static values = {
    currentUserId: Number,
    moderator: Boolean,
    contextType: String,
    contextId: Number,
    heartbeatIntervalMs: Number,
    subscriberProfileBaseUrl: String,
    // Set only by the live console, where the banner is editable in place. An editable banner has
    // to stay on screen when empty, or there is nothing left for the creator to click.
    announcementEditable: Boolean,
  };

  connect() {
    this.closed = false;
    this.timedOut = false;
    this.timeoutTimer = null;
    this.selectedTimeoutSeconds = TIMEOUT_DEFAULT_SECONDS;
    this.announcementExpanded = false;
    // client_msg_id -> { body, row, timer, resent, failed }. An entry exists from send() until its
    // echo arrives (see received()'s "message" branch) or it's abandoned as failed.
    this.pendingMessages = new Map();
    // At most one forced consumer.connection.reopen() at a time; see reconnectIfNeeded().
    this.reconnectInFlight = false;
    this.reconnectInFlightTimer = null;
    // At most one in-flight manual reconnect() subscribe attempt at a time; see reconnect().
    this.reconnectPending = false;
    // Tracks whether the reader was at the bottom of the list, kept current via the scroll
    // listener below. Initialized eagerly (not left undefined) so the very first keyboard-open
    // resize on mobile already has a valid value to re-pin against.
    this.pinnedToBottom = true;

    // Stimulus singular target getters are not memoized (each access re-queries the
    // controller's subtree, which only grows as messages accumulate for the session), so
    // resolve these once and reuse the cached elements from the per-message build methods.
    this.messageTemplateEl = this.messageTemplateTarget;
    this.pendingMessageTemplateEl = this.pendingMessageTemplateTarget;
    this.tipMessageTemplateEl = this.tipMessageTemplateTarget;
    this.privateMessageTemplateEl = this.privateMessageTemplateTarget;
    if (this.hasModeratorActionsTemplateTarget) this.moderatorActionsTemplateEl = this.moderatorActionsTemplateTarget;
    if (this.hasTipModeratorActionsTemplateTarget) this.tipModeratorActionsTemplateEl = this.tipModeratorActionsTemplateTarget;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    this.confirmOpenedAt = 0;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    this.onConfirmKeydown = (event) => {
      if (!this.pendingConfirm) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.cancelConfirm();
        return;
      }
      if (event.key !== "Tab") return;

      event.preventDefault();
      const stops = this.confirmFocusableElements();
      if (stops.length === 0) return;

      const currentIndex = stops.indexOf(document.activeElement);
      const nextIndex = (currentIndex + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
      stops[nextIndex].focus();
    };
    document.addEventListener("keydown", this.onConfirmKeydown);

    this.subscription = this.createSubscription();

    // The announcement chevron only appears when the 2-line-clamped text overflows;
    // re-check on resize because the chat panel width changes across breakpoints.
    this.onResize = () => this.scheduleAnnouncementToggleRefresh();
    window.addEventListener("resize", this.onResize);
    this.scheduleAnnouncementToggleRefresh();

    // Keep pinnedToBottom current as the reader scrolls, so a later resize (e.g. the mobile
    // keyboard opening/closing) knows whether it should re-pin.
    this.onMessagesScroll = () => { this.pinnedToBottom = this.isPinnedToBottom(); };
    this.messagesTarget.addEventListener("scroll", this.onMessagesScroll, { passive: true });

    // The visual viewport (not window) resizes when the on-screen keyboard opens/closes on
    // mobile; re-pin to the bottom across that resize so a pinned reader never loses their place.
    this.onViewportResize = () => {
      if (this.pinnedToBottom) {
        window.requestAnimationFrame(() => {
          if (this.hasMessagesTarget) this.scrollToBottom();
        });
      }
    };
    if (window.visualViewport) window.visualViewport.addEventListener("resize", this.onViewportResize);

    // Keep the server-side presence entry fresh. The interval comes from
    // LiveViewerPresence::HEARTBEAT_INTERVAL and must stay well under the server's
    // PRESENCE_TTL (45s) so a tab is never evicted while open. The immediate ping
    // fires from onConnected (once the channel is actually up). Guarded (not cleared) across a
    // subscribe-time rejection, so a later reconnect() resumes ticking without recreating it.
    this.heartbeat = window.setInterval(() => {
      if (this.subscription) this.subscription.perform("heartbeat");
    }, this.heartbeatIntervalMsValue);

    // Re-register promptly after the browser throttles background timers, and confirm the
    // connection actually survived rather than just pinging into it blind (see confirmConnectionAlive).
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.confirmConnectionAlive();
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    // A Turbo-restored page can have a pre-filled input, so render a correct counter immediately.
    this.updateCounter();
  }

  // Shared by connect() and reconnect() (the rejected-subscription retry) so the two can't drift.
  createSubscription() {
    return consumer.subscriptions.create(
      {
        channel: "LiveStreamChannel",
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      },
      {
        received: this.received.bind(this),
        connected: this.onConnected.bind(this),
        disconnected: this.onDisconnected.bind(this),
        rejected: this.onRejected.bind(this),
      }
    );
  }

  disconnect() {
    this.stopRealtime();
    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onMessagesScroll && this.hasMessagesTarget) this.messagesTarget.removeEventListener("scroll", this.onMessagesScroll);
    if (window.visualViewport && this.onViewportResize) window.visualViewport.removeEventListener("resize", this.onViewportResize);
    if (this.onConfirmKeydown) document.removeEventListener("keydown", this.onConfirmKeydown);
  }

  // Shared by disconnect() and handleRoomClosed(): drops the cable subscription and stops the
  // presence heartbeat. Both are terminal for this controller instance, so the visibilitychange
  // listener is removed here too rather than left to fire (harmlessly, post-guard) forever.
  stopRealtime() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
      this.subscription = null;
    }
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    if (this.onVisibilityChange) document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.timeoutTimer) {
      window.clearInterval(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    this.haltAllPending();
  }

  // None of stopRealtime()/onRejected() leave the channel in a state where an automatic resend
  // could ever land, so every still-pending message is halted and surfaced as failed (with its
  // usual retry affordance) rather than left stuck at "Sending..." forever.
  // Also cancels a still-running confirmConnectionAlive() timer for the same reason: onRejected()
  // in particular nulls this.subscription, and a stray timer firing after that would call
  // reconnectIfNeeded() and never get its reconnectInFlight latch released.
  haltAllPending() {
    this.pendingMessages.forEach((pending) => {
      if (pending.timer) window.clearTimeout(pending.timer);
      pending.timer = null;
      if (!pending.failed) {
        pending.failed = true;
        this.setPendingRowState(pending.row, "failed");
      }
    });
    if (this.confirmAliveTimer) {
      window.clearTimeout(this.confirmAliveTimer);
      this.confirmAliveTimer = null;
    }
    this.clearReconnectInFlight();
    this.updateSendFailedNotice();
  }

  // The room has closed (live-pages--show#closeRoom, either kind) - wind the live parts of the
  // panel down in place.
  handleRoomClosed() {
    this.stopRealtime();
    this.setInputDisabled(true);
    this.closed = true;
    if (this.hasClosedTarget) this.closedTarget.classList.remove("hidden");
  }

  onConnected() {
    this.reconnectPending = false;
    this.enableInput();
    // Register presence immediately on (re)connect, before the first interval tick.
    this.subscription.perform("heartbeat");
    this.resendPendingMessages();
  }

  onDisconnected() {
    this.setInputDisabled(true);
  }

  // A subscribe-time rejection (auth failure, room not found, etc.) - distinct from a per-message
  // speak rejection (see received()'s "rejected" case), which has its own handling already. The
  // cable side has already forgotten this subscription by the time this fires (see Subscriptions#
  // reject in @rails/actioncable), so there is nothing left to unsubscribe.
  onRejected() {
    if (this.closed) return;

    this.reconnectPending = false;
    // The heartbeat interval is left running (its own guard no-ops while subscription is null),
    // so a later reconnect() resumes ticking without recreating it.
    this.haltAllPending();
    this.subscription = null;
    this.setInputDisabled(true);
    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, true);
  }

  // Wired from the disconnected notice's retry button - attempts a fresh subscribe. The composer
  // stays disabled until the new subscription's own onConnected() re-enables it. Guarded against a
  // fast double-click/tap orphaning an earlier, never-removed subscription.
  reconnect() {
    if (this.reconnectPending) return;
    this.reconnectPending = true;

    if (this.hasDisconnectedTarget) this.setNoticeVisible(this.disconnectedTarget, false);
    this.subscription = this.createSubscription();
  }

  // On returning to the foreground, actively confirm the connection survived rather than waiting
  // for a stale-connection timeout or a failed send to notice it - iOS can silently kill sockets
  // while backgrounded, and readyState alone can't tell us. Reuses the same "no ack in time means
  // assume dead" detector a send timeout already uses, just with no message attached to it, so a
  // healthy connection (the common case) round-trips well under the timeout and nothing visible
  // happens - only a genuinely dead one ever triggers reconnectIfNeeded()'s forced reopen.
  confirmConnectionAlive() {
    if (!this.subscription || this.confirmAliveTimer) return;

    this.subscription.perform("heartbeat", { want_pong: true });
    this.confirmAliveTimer = window.setTimeout(() => {
      this.confirmAliveTimer = null;
      this.reconnectIfNeeded();
    }, SEND_ACK_TIMEOUT_MS);
  }

  // Shared hidden/flex visibility toggle, matching the classList.toggle idiom
  // updateSendFailedNotice already uses.
  setNoticeVisible(target, visible) {
    target.classList.toggle("hidden", !visible);
    target.classList.toggle("flex", visible);
  }

  received(data) {
    switch (data.type) {
      case "backfill":
        this.messagesTarget.replaceChildren();
        (data.messages || []).forEach((message) => this.appendMessage(message));
        // A backfilled message that matches a pending one means it actually made it to the server;
        // resolve it here too, or it would be left as a permanently-unresolved ghost row below.
        (data.messages || []).forEach((message) => {
          const pending = this.pendingMessages.get(message.client_msg_id);
          if (pending) this.resolvePendingMessage(message.client_msg_id, pending);
        });
        // replaceChildren() above wiped any pending/failed rows too - they're client-side-only state.
        this.rerenderPendingMessages();
        this.scrollToBottom();
        break;
      case "message": {
        // Our own message echoed back always scrolls into view, even if the reader had scrolled
        // up before sending. `pinned` must be computed here, before appendMessage() below changes
        // scrollHeight - isPinnedToBottom() after the append would no longer reflect pre-send state.
        const pending = this.pendingMessages.get(data.client_msg_id);
        const mine = !!pending;
        const pinned = mine || this.isPinnedToBottom();
        // appendMessage() first: if resolving the pending row below throws, the real message has
        // already landed rather than being lost.
        this.appendMessage(data);
        if (pending) this.resolvePendingMessage(data.client_msg_id, pending);
        if (pinned) this.scrollToBottom();
        break;
      }
      case "remove":
        this.removeMessage(data.client_msg_id);
        break;
      case "remove_user":
        this.handleRemoveUser(data.user_id);
        break;
      case "timed_out":
        this.handleTimedOut(data.seconds_remaining);
        break;
      case "private_message":
        this.appendPrivateMessage(data);
        break;
      case "viewers":
        this.updateViewerCount(data.count);
        break;
      case "pong":
        if (this.confirmAliveTimer) {
          window.clearTimeout(this.confirmAliveTimer);
          this.confirmAliveTimer = null;
        }
        break;
      case "rejected":
        // A per-message speak rejection - not the subscribe-time rejection onRejected() handles.
        // client_msg_id may be absent for the rare rejection with no id attached.
        if (data.client_msg_id != null) this.cancelPendingMessage(data.client_msg_id);
        if (data.reason === "slow_mode") {
          this.handleSlowMode();
        } else if (data.reason === "timed_out") {
          this.handleTimedOut(data.seconds_remaining);
        } else {
          this.showHint("Something went wrong sending your message. Try again.");
        }
        break;
      case "announcement":
        this.setAnnouncement(data.body, data.body_html);
        break;
      case "poll":
        // Forward poll events to the floating card (window event so the card may be absent).
        this.dispatch("poll", { detail: data, target: window });
        break;
      case "tip":
        // A tip renders in BOTH places (deduped by id): highlighted inline AND in the pinned tip
        // rail. The rail may be absent (dashboard / flag off), so forward via a window event.
        if (data.action === "created") {
          this.addInlineTip(data.tip);
          this.dispatch("tip", { detail: data.tip, target: window });
        } else if (data.action === "removed") {
          const id = data.tip && data.tip.id;
          this.removeInlineTip(id);
          this.dispatch("tip-removed", { detail: { id }, target: window });
        }
        break;
      case "tips_backfill":
        // Subscribe-time backfill (latest-first) for the pinned tip rail only; the inline scroll
        // is rebuilt from the chat backfill and does not replay past tips.
        this.dispatch("tips-backfill", { detail: data.tips || [], target: window });
        break;
    }
  }

  // Count renders twice (mobile span + desktop pill, both outside this controller's element) -
  // a single querySelector only ever hit the first (mobile) node, freezing the other viewport's.
  updateViewerCount(count) {
    document.querySelectorAll("[data-live-viewer-count]").forEach((el) => {
      el.setAttribute("data-rolling-number-count-value", count);
    });
  }

  // Cast a vote for the floating card over our shared subscription (it has none of its own).
  castVote(event) {
    if (!this.subscription) return;

    this.reportActionSent(this.subscription.perform("vote", {
      poll_id: event.detail.pollId,
      option_id: event.detail.optionId,
    }));
  }

  setAnnouncement(body, bodyHtml) {
    if (!this.hasAnnouncementTarget) return;

    const text = body || "";
    // body_html is server-escaped + linkified, so it's safe to inject; fall back to plain text if an older payload omits it.
    if (bodyHtml != null) {
      this.announcementBodyTarget.innerHTML = bodyHtml;
    } else {
      this.announcementBodyTarget.textContent = text;
    }
    // This is the single authority on what the banner reads, including for the creator who just
    // typed it: LiveEventsController#update broadcasts on save, so the inline editor never writes
    // the text itself and the linkified markup can't be raced away by a plain-text local write.
    const blank = text.trim() === "";
    this.announcementBodyTarget.hidden = blank;
    if (this.hasAnnouncementPlaceholderTarget) this.announcementPlaceholderTarget.hidden = !blank;

    this.announcementTarget.classList.toggle("hidden", blank && !this.announcementEditableValue);
    this.collapseAnnouncement();
    this.scheduleAnnouncementToggleRefresh();
  }

  toggleAnnouncement() {
    this.announcementExpanded = !this.announcementExpanded;
    this.announcementTextTarget.classList.toggle("line-clamp-2", !this.announcementExpanded);
    this.announcementToggleTarget.classList.toggle("rotate-180", this.announcementExpanded);
    this.announcementToggleTarget.setAttribute("aria-expanded", String(this.announcementExpanded));
  }

  collapseAnnouncement() {
    this.announcementExpanded = false;
    if (this.hasAnnouncementTextTarget) this.announcementTextTarget.classList.add("line-clamp-2");
    if (this.hasAnnouncementToggleTarget) {
      this.announcementToggleTarget.classList.remove("rotate-180");
      this.announcementToggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  scheduleAnnouncementToggleRefresh() {
    window.requestAnimationFrame(() => this.refreshAnnouncementToggle());
  }

  // Show the expand chevron only when the clamped text overflows two lines. While
  // expanded the chevron stays (it's the collapse control), so only measure when collapsed.
  refreshAnnouncementToggle() {
    if (!this.hasAnnouncementToggleTarget || !this.hasAnnouncementTextTarget) return;
    if (this.announcementExpanded) return;

    const el = this.announcementTextTarget;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    this.announcementToggleTarget.classList.toggle("hidden", !overflowing);
  }

  send(event) {
    event.preventDefault();

    const body = this.inputTarget.value.trim();
    if (body === "") return;

    // Defense in depth: setInputDisabled normally keeps this unreachable (handleRoomClosed nulls
    // the subscription in the same breath as disabling the composer), but a null subscription must
    // never reach perform() below - that throws inside the Stimulus action handler and the typed
    // message vanishes with no feedback at all.
    if (!this.subscription) {
      this.showHint("Chat isn't connected. Reload the page to send your message.");
      return;
    }

    // Generated once per message, even across a timeout/reconnect/resend or a manual retry: the
    // same id is tracked in pendingMessages (so received() can recognize our own echo) and sent as
    // client_msg_id, so a resend reusing it lets each client's own appendMessage() DOM-presence
    // check absorb the duplicate echo of a message that actually did arrive the first time - a
    // fresh id on resend would double-post it instead. There is no server-side dedupe.
    const clientMsgId = this.newClientMsgId();
    const row = this.buildPendingMessage(clientMsgId, body);
    this.messagesTarget.appendChild(row);
    // Our own outgoing message always scrolls into view, same as its echo does in received().
    this.scrollToBottom();

    this.pendingMessages.set(clientMsgId, { body, row, timer: null, resent: false, failed: false });
    this.performSpeak(clientMsgId, body);
    this.startPendingTimer(clientMsgId);

    this.clearInput();
    this.focusInput();
  }

  // Shared by send(), the reconnect resend sweep, and retrySend() - the boolean return only tells
  // us the raw transport was definitely closed (not merely half-open), which still needs the same
  // timeout/reconnect/resend handling as silence, so it's surfaced but not special-cased out of it.
  performSpeak(clientMsgId, body) {
    const sent = this.subscription.perform("speak", { body, client_msg_id: clientMsgId });
    if (sent === false) this.showHint("Connection trouble - resending your message…");
  }

  startPendingTimer(clientMsgId, timeoutMs = SEND_ACK_TIMEOUT_MS) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;
    if (pending.timer) window.clearTimeout(pending.timer);
    pending.timer = window.setTimeout(() => this.handleSendTimeout(clientMsgId), timeoutMs);
  }

  // No echo within SEND_ACK_TIMEOUT_MS. First timeout: force a reconnect and wait out the longer
  // RECONNECT_TIMEOUT_MS for it to actually happen (resendPendingMessages() re-arms the tight
  // SEND_ACK_TIMEOUT_MS once genuinely resent). Second timeout (still pending after that): give up.
  handleSendTimeout(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (pending.resent) {
      this.failMessage(clientMsgId);
      return;
    }

    pending.resent = true;
    this.startPendingTimer(clientMsgId, RECONNECT_TIMEOUT_MS);
    this.reconnectIfNeeded();
  }

  // Force the transport itself closed and reopened - a half-open socket still reports OPEN, so a
  // resubscribe alone would keep writing into the same dead connection. reopen() closes the local
  // WebSocket outright (that doesn't depend on the dead remote end), so it reliably moves off
  // OPEN/CONNECTING even when the close handshake with the server never completes.
  reconnectIfNeeded() {
    if (this.reconnectInFlight) return;
    this.reconnectInFlight = true;
    consumer.connection.reopen();

    // Safety valve: if this attempt never itself produces a connect or a rejection (both of which
    // already reset the flag elsewhere), don't let it wedge auto-recovery off for the rest of the
    // session - a later timeout's own reconnect attempt is better than none ever happening again.
    if (this.reconnectInFlightTimer) window.clearTimeout(this.reconnectInFlightTimer);
    this.reconnectInFlightTimer = window.setTimeout(() => this.clearReconnectInFlight(), RECONNECT_TIMEOUT_MS * 3);
  }

  // Resets the forced-reopen latch and its safety-valve timer together, wherever the latch is legitimately cleared.
  clearReconnectInFlight() {
    if (this.reconnectInFlightTimer) window.clearTimeout(this.reconnectInFlightTimer);
    this.reconnectInFlightTimer = null;
    this.reconnectInFlight = false;
  }

  // Runs on every onConnected() (forced reopen or otherwise): resend whatever is still pending, on
  // the (possibly new) connection, reusing each id. Reusing the id makes this safe to run whenever
  // a reconnect happens, not only ones this controller triggered itself.
  resendPendingMessages() {
    if (!this.subscription) return;

    this.pendingMessages.forEach((pending, clientMsgId) => {
      if (pending.failed) return;
      pending.resent = true;
      this.performSpeak(clientMsgId, pending.body);
      this.startPendingTimer(clientMsgId);
    });
    this.clearReconnectInFlight();
  }

  // The echo arrived: the message is confirmed, so drop its pending row and let the resend guard
  // free up in case the reconnect it was waiting on never actually completed.
  resolvePendingMessage(clientMsgId, pending) {
    if (pending.timer) window.clearTimeout(pending.timer);
    this.pendingMessages.delete(clientMsgId);
    pending.row.remove();
    this.updateSendFailedNotice();
  }

  // A rejection, not a delivery or a timeout: just stop waiting, with no confirmed row and no retry affordance.
  cancelPendingMessage(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (pending.timer) window.clearTimeout(pending.timer);
    this.pendingMessages.delete(clientMsgId);
    pending.row.remove();
    this.updateSendFailedNotice();
  }

  // The resend also timed out - stop retrying automatically and surface a manual retry instead.
  failMessage(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    pending.failed = true;
    this.clearReconnectInFlight();
    this.setPendingRowState(pending.row, "failed");
    this.updateSendFailedNotice();
  }

  // Wired from both the in-row retry control (which passes its own client_msg_id) and the
  // composer-adjacent failed-send notice (which has none, so it retries every failed message).
  retryMessage(event) {
    const clientMsgId = event.params && event.params.clientMsgId;
    const ids = clientMsgId
      ? [clientMsgId]
      : Array.from(this.pendingMessages.keys()).filter((id) => this.pendingMessages.get(id).failed);

    ids.forEach((id) => this.retrySend(id));
  }

  retrySend(clientMsgId) {
    const pending = this.pendingMessages.get(clientMsgId);
    if (!pending) return;

    if (!this.subscription) {
      this.showHint("Chat isn't connected. Reload the page to send your message.");
      return;
    }

    pending.failed = false;
    pending.resent = false;
    this.setPendingRowState(pending.row, "pending");
    this.updateSendFailedNotice();
    this.performSpeak(clientMsgId, pending.body);
    this.startPendingTimer(clientMsgId);
  }

  // Re-appends every still-pending message after a backfill wipes the message list, since pending
  // rows are client-side-only state that the backfill payload knows nothing about.
  rerenderPendingMessages() {
    this.pendingMessages.forEach((pending, clientMsgId) => {
      pending.row = this.buildPendingMessage(clientMsgId, pending.body);
      this.setPendingRowState(pending.row, pending.failed ? "failed" : "pending");
      this.messagesTarget.appendChild(pending.row);
    });
  }

  // Clones pendingMessageTemplate. Marked with data-pending-client-msg-id (never data-client-msg-id
  // - appendMessage()'s dedupe matches that selector, and colliding here would silently suppress
  // the authoritative row when its echo arrives, the exact bug this whole feature exists to fix).
  buildPendingMessage(clientMsgId, body) {
    const wrapper = this.pendingMessageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.pendingClientMsgId = clientMsgId;
    wrapper.querySelector("[data-chat-message-body]").textContent = body;
    wrapper.querySelector("[data-chat-pending-retry-button]").dataset.chatPanelClientMsgIdParam = clientMsgId;
    return wrapper;
  }

  setPendingRowState(row, state) {
    const failed = state === "failed";
    row.classList.toggle("opacity-60", !failed);

    row.querySelector("[data-chat-pending-status]").classList.toggle("hidden", failed);

    const failedEl = row.querySelector("[data-chat-pending-failed]");
    failedEl.classList.toggle("hidden", !failed);
    failedEl.classList.toggle("flex", failed);
  }

  updateSendFailedNotice() {
    if (!this.hasSendFailedNoticeTarget) return;

    const anyFailed = Array.from(this.pendingMessages.values()).some((pending) => pending.failed);
    this.sendFailedNoticeTarget.classList.toggle("hidden", !anyFailed);
    this.sendFailedNoticeTarget.classList.toggle("flex", anyFailed);
  }

  // Keeps the on-screen keyboard open on mobile across a send, since blurring the input closes it.
  focusInput() {
    if (this.hasInputTarget && !this.inputTarget.disabled) this.inputTarget.focus();
  }

  // Clears the input programmatically (a plain value reset fires no `input` event, so the
  // counter is updated explicitly here). Called both from send() above and, cross-controller,
  // from chat-tip's onSucceeded() after a successful tip.
  clearInput() {
    this.inputTarget.value = "";
    this.updateCounter();
  }

  // Shows "<length>/<max>" only once the message is within COUNTER_VISIBLE_REMAINING characters
  // of the input's maxlength (read from the DOM so the limit is never declared twice on the
  // client). max <= 0 means no maxlength attribute is set (browsers report -1), which would
  // otherwise satisfy the threshold immediately.
  updateCounter() {
    if (!this.hasInputTarget || !this.hasCounterTarget) return;

    const max = this.inputTarget.maxLength;
    if (max <= 0) return;

    const length = this.inputTarget.value.length;
    const remaining = max - length;
    this.counterTarget.classList.toggle("hidden", remaining > COUNTER_VISIBLE_REMAINING);
    this.counterTarget.textContent = `${length}/${max}`;
  }

  deleteMessage(event) {
    if (!this.moderatorValue) return;

    const clientMsgId = event.params.clientMsgId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "delete",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => this.subscription.perform("delete_message", { client_msg_id: clientMsgId }),
    });
  }

  banUser(event) {
    if (!this.moderatorValue) return;

    const userId = event.params.userId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "ban",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => this.subscription.perform("ban_user", { user_id: userId }),
    });
  }

  // Moderator time-out from the rail's "Time out" action. Unlike ban/delete, `run` reads two more
  // pieces of state live at accept time - the selected duration chip and the reason textarea - both
  // of which the user can only have set after this closure was built (openConfirm resets them, then
  // the dialog sits open for the moderator to change them, then acceptConfirm calls this closure).
  timeOutUser(event) {
    if (!this.moderatorValue) return;

    const userId = event.params.userId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "timeout",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => this.subscription.perform("timeout_user", {
        user_id: userId,
        seconds: this.selectedTimeoutSeconds,
        reason: this.hasTimeoutReasonTarget ? this.timeoutReasonTarget.value.trim() : "",
      }),
    });
  }

  // Moderator remove from the inline tip's toolbar (params-based, like deleteMessage).
  removeTip(event) {
    if (!this.moderatorValue) return;

    const row = this.rowFor(event);
    this.confirmRemoveTip(event.params.tipId, { trigger: event.currentTarget, preview: row ? this.previewFromRow(row) : null });
  }

  // Moderator remove from the pinned tip rail. The rail owns no subscription, so it asks us (which does) via a window event.
  removeRailTip(event) {
    if (!this.moderatorValue) return;

    const detail = event.detail || {};
    this.confirmRemoveTip(detail.id, { trigger: detail.trigger || null, preview: detail.tip ? this.previewFromTip(detail.tip) : null });
  }

  // Shared by both tip removal paths: the server unpins + removes it for everyone; no refund.
  confirmRemoveTip(tipId, { trigger, preview } = {}) {
    if (tipId == null) return;

    this.openConfirm({
      variant: "tip",
      preview,
      trigger,
      run: () => this.subscription.perform("remove_tip", { id: tipId }),
    });
  }

  openConfirm({ variant, preview, trigger, run }) {
    if (!this.hasConfirmTarget) {
      this.reportActionSent(run());
      return;
    }
    if (this.pendingConfirm) return;

    this.pendingConfirm = { run };
    this.confirmTrigger = trigger || null;
    this.confirmOpenedAt = Date.now();
    this.showConfirmVariant(variant);
    // Reset on open, never on close: acceptConfirm() closes the dialog before running the pending
    // action, so a close-time reset would wipe the chip/textarea state before that action reads it.
    if (variant === "timeout") this.resetTimeoutFields(preview);
    this.fillConfirmPreview(preview);
    this.confirmTarget.classList.remove("hidden");
    // Cancel takes focus, not Confirm, so a reflex Enter mid-composer can't fire the destructive action.
    this.confirmCancelTarget.focus();
  }

  // Timeout-only reset: the default 10-minute chip, an empty reason, and the target's name filled
  // into the title (there is no server-side name to interpolate - the target is chosen client-side).
  resetTimeoutFields(preview) {
    this.selectedTimeoutSeconds = TIMEOUT_DEFAULT_SECONDS;
    if (this.hasTimeoutReasonTarget) this.timeoutReasonTarget.value = "";
    this.timeoutDurationChipTargets.forEach((chip) => {
      this.setTimeoutChipActive(chip, Number(chip.dataset.chatPanelSecondsParam) === this.selectedTimeoutSeconds);
    });
    if (this.hasConfirmTitleNameTarget) this.confirmTitleNameTarget.textContent = (preview && preview.name) || "this person";
  }

  selectTimeoutDuration(event) {
    const seconds = Number(event.params.seconds);
    if (!TIMEOUT_DURATIONS.includes(seconds)) return;

    this.selectedTimeoutSeconds = seconds;
    this.timeoutDurationChipTargets.forEach((chip) => {
      this.setTimeoutChipActive(chip, Number(chip.dataset.chatPanelSecondsParam) === seconds);
    });
  }

  setTimeoutChipActive(chip, active) {
    chip.setAttribute("aria-pressed", String(active));
    chip.classList.toggle("border-accent", active);
    chip.classList.toggle("bg-accent/10", active);
    chip.classList.toggle("text-accent", active);
    chip.classList.toggle("border-gray-200", !active);
    chip.classList.toggle("text-gray-600", !active);
  }

  acceptConfirm() {
    const pending = this.pendingConfirm;
    this.closeConfirm();

    if (!pending || !this.moderatorValue || !this.subscription) return;

    this.reportActionSent(pending.run());
  }

  // perform()'s false return means the transport wasn't actually OPEN and the action never reached the server.
  reportActionSent(sent) {
    if (sent === false) this.showHint("Connection trouble - try again.");
  }

  cancelConfirm() {
    this.closeConfirm();
  }

  confirmBackdropClick(event) {
    if (event.currentTarget !== event.target) return;
    if (Date.now() - this.confirmOpenedAt < CONFIRM_DISMISS_GRACE_MS) return;

    this.cancelConfirm();
  }

  closeConfirm() {
    const trigger = this.confirmTrigger;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    // The Tab-trap and the click-intercepting backdrop mean focus cannot legitimately leave the
    // dialog while it's open, so restoring it here doesn't need to reactively check where focus
    // currently sits (which a backdrop click's own mousedown has already blurred to <body>).
    if (trigger && trigger.isConnected) trigger.focus();
  }

  showConfirmVariant(variant) {
    this.confirmTarget.querySelectorAll("[data-chat-confirm-variant]").forEach((el) => {
      el.hidden = el.dataset.chatConfirmVariant !== variant;
    });
  }

  // A live query, not a hardcoded stop list: showConfirmVariant() above is the only thing that
  // decides what's actually showing (it flips `hidden` on whichever [data-chat-confirm-variant]
  // wrapper isn't the active one), so filtering on `!el.closest("[hidden]")` tracks that mechanism
  // directly - the timeout variant's duration chips and reason textarea join the trap automatically
  // while their wrapper is the one left un-hidden, with no separate wiring needed here.
  confirmFocusableElements() {
    const dialog = this.confirmTarget.querySelector('[role="dialog"]');
    if (!dialog) return [];

    return Array.from(dialog.querySelectorAll(CONFIRM_FOCUSABLE_SELECTOR)).filter(
      (el) => !el.disabled && !el.closest("[hidden]")
    );
  }

  fillConfirmPreview(preview) {
    const show = !!preview;
    this.confirmPreviewTarget.classList.toggle("hidden", !show);
    this.confirmPreviewTarget.classList.toggle("flex", show);
    if (!show) return;

    applyAvatar(this.confirmPreviewAvatarTarget, this.confirmPreviewAvatarFallbackTarget, preview.user);
    this.confirmPreviewNameTarget.textContent = preview.name || "";
    this.confirmPreviewBodyTarget.textContent = preview.body || "";

    const amount = preview.amount || "";
    this.confirmPreviewAmountTarget.textContent = amount;
    this.confirmPreviewAmountTarget.hidden = amount === "";
  }

  rowFor(event) {
    return event.currentTarget.closest("[data-client-msg-id], [data-tip-id]");
  }

  previewFromRow(row) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    const bodyEl = row.querySelector("[data-chat-message-body]");
    const amountEl = row.querySelector("[data-chat-tip-amount]");
    const avatarEl = row.querySelector("[data-chat-message-avatar]");
    const fallbackEl = row.querySelector("[data-chat-message-avatar-fallback]");

    return {
      name: nameEl ? nameEl.textContent : "",
      body: bodyEl ? bodyEl.textContent : "",
      amount: amountEl ? amountEl.textContent : "",
      user: {
        avatar_url: avatarEl && !avatarEl.classList.contains("hidden") ? avatarEl.src : null,
        initials: fallbackEl ? fallbackEl.textContent : "",
        avatar_color_class: (fallbackEl && fallbackEl.dataset.avatarColorClass) || null,
      },
    };
  }

  previewFromTip(tip) {
    return {
      name: (tip.user && tip.user.name) || "",
      body: (tip.body || "").trim(),
      amount: this.tipAmountLabel(tip),
      user: tip.user || null,
    };
  }

  // Shared by the inline tip row's badge and the confirm preview, so the two can't format differently.
  tipAmountLabel(tip) {
    return `TIP ${formatMoney(tip.amount_cents, tip.currency)}`;
  }

  appendMessage(message) {
    if (this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(message.client_msg_id)}"]`)) return;

    this.messagesTarget.appendChild(this.buildMessage(message));
  }

  // Clones messageTemplate (declared in the panel markup) and fills its slots. Markup lives
  // in the ERB template; this only ever populates it.
  buildMessage(message) {
    const wrapper = this.messageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.clientMsgId = message.client_msg_id;
    wrapper.dataset.userId = message.user.id;

    this.fillAvatar(wrapper, message.user);
    this.fillIdentity(wrapper, message.user.name, !!message.user.is_channel_admin, message.user.id, !!message.user.has_subscriber_profile);
    this.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!message.user.is_moderator);

    if (this.moderatorValue) wrapper.appendChild(this.buildModeratorActions(message));

    return wrapper;
  }

  // Shared by buildMessage and buildTipMessage: shows the real photo when avatar_url is present,
  // otherwise the colored-initials fallback ChatSenderIdentity computed server-side (so it matches
  // AvatarComponent elsewhere in the app).
  fillAvatar(row, user) {
    applyAvatar(
      row.querySelector("[data-chat-message-avatar]"),
      row.querySelector("[data-chat-message-avatar-fallback]"),
      user
    );
  }

  // Shared by buildMessage and buildTipMessage: sets the display name (a new-tab link to the
  // sender's dashboard profile for moderators only) and, for a channel admin, appends the cloned
  // moderator badge into the identity slot already in the row.
  fillIdentity(row, name, isChannelAdmin, userId, hasSubscriberProfile) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    nameEl.className = `font-semibold ${isChannelAdmin ? "text-accent" : "text-strong-text"}`;

    if (this.moderatorValue && hasSubscriberProfile && userId != null) {
      // Only link a sender who actually has a subscriber profile here - not "isn't a channel
      // admin", which a platform admin with no Subscription would also satisfy and then 404.
      // textContent (never innerHTML) keeps the user-controlled display name XSS-safe.
      const link = document.createElement("a");
      link.setAttribute("href", `${this.subscriberProfileBaseUrlValue}/${userId}`);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener");
      link.className = "text-inherit hover:underline";
      link.textContent = name;
      nameEl.replaceChildren(link);
    } else {
      nameEl.textContent = name;
    }

    if (isChannelAdmin) row.querySelector("[data-chat-message-identity]").appendChild(this.buildModeratorBadge());
  }

  // Renders the message body. For moderators, bare https?:// URLs become new-tab links; everyone
  // else's body is plain text (identical to before). Anchors are built via createElement + textContent
  // (never innerHTML), and only https?:// matches, so pasted javascript:/data:/HTML can never become a link.
  fillBody(bodyEl, body, senderIsModerator) {
    const text = body || "";
    if (!senderIsModerator) {
      bodyEl.textContent = text;
      return;
    }
    bodyEl.replaceChildren();
    const regex = new RegExp(CHAT_URL_REGEX.source, "gi"); // fresh instance: global regex is stateful
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      let url = match[0];
      // Don't swallow trailing sentence punctuation, e.g. "see https://x.com." or "(https://x.com)".
      const trailing = url.match(/[.,;:!?)\]}'">]+$/);
      if (trailing) url = url.slice(0, url.length - trailing[0].length);
      const start = match.index;
      const end = start + url.length;
      if (start > lastIndex) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer nofollow");
      link.className = "text-accent underline";
      link.textContent = url;
      bodyEl.appendChild(link);
      lastIndex = end;
      regex.lastIndex = end; // resume after the trimmed URL so trailing punctuation is re-emitted as text
    }
    if (lastIndex < text.length) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  buildModeratorBadge() {
    const badge = document.createElement("span");
    badge.className = "inline-flex items-center";
    badge.setAttribute("role", "img");
    badge.setAttribute("aria-label", "Moderator");
    badge.title = "Moderator";
    badge.appendChild(this.moderatorBadgeTarget.content.cloneNode(true));
    badge.querySelectorAll("svg, img").forEach((el) => el.setAttribute("aria-hidden", "true"));
    return badge;
  }

  // A small toolbar that floats over the top-right of the message and only appears on hover, so
  // it never reserves column width in the chat. Hidden + non-interactive until the row is hovered
  // or focused. Collapsed to just the "..." trigger at rest (see moderatorActionsTemplate); the
  // three labelled actions inside are revealed by expandRailActions/toggleRailActions below. Icons
  // are cloned from the IconComponent-rendered <template>s in the panel markup; the toolbar shell
  // itself is also cloned from a <template> (moderatorActionsTemplate).
  buildModeratorActions(message) {
    const actions = this.moderatorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-delete-button]");
    remove.querySelector("[data-chat-rail-icon]").appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelClientMsgIdParam = message.client_msg_id;

    const timeout = actions.querySelector("[data-chat-message-timeout-button]");
    timeout.querySelector("[data-chat-rail-icon]").appendChild(this.timeoutIconTarget.content.cloneNode(true));
    timeout.dataset.chatPanelUserIdParam = message.user.id;

    const ban = actions.querySelector("[data-chat-message-ban-button]");
    ban.querySelector("[data-chat-rail-icon]").appendChild(this.banIconTarget.content.cloneNode(true));
    ban.dataset.chatPanelUserIdParam = message.user.id;

    actions.querySelector("[data-chat-rail-ellipsis]").appendChild(this.ellipsisIconTarget.content.cloneNode(true));

    return actions;
  }

  // mouseenter/mouseleave on the rail container (desktop hover).
  expandRailActions(event) {
    this.setRailExpanded(event.currentTarget, true);
  }

  collapseRailActions(event) {
    this.setRailExpanded(event.currentTarget, false);
  }

  // click on the "..." trigger - covers touch tap and keyboard activation (Enter/Space on a
  // focused <button> fires a click event natively, so this needs no separate keyboard handling).
  toggleRailActions(event) {
    const rail = event.currentTarget.closest("[data-chat-rail]");
    if (!rail) return;

    this.setRailExpanded(rail, rail.dataset.expanded !== "true");
  }

  setRailExpanded(rail, expanded) {
    rail.dataset.expanded = String(expanded);
    rail.querySelectorAll("[data-chat-rail-action]").forEach((button) => { button.hidden = !expanded; });

    const ellipsis = rail.querySelector("[data-chat-rail-ellipsis]");
    if (ellipsis) ellipsis.setAttribute("aria-expanded", String(expanded));
  }

  removeMessage(clientMsgId) {
    const node = this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(clientMsgId)}"]`);
    if (node) node.remove();
  }

  // Append a tip as a highlighted inline message, deduped by tip id so a redelivered tip
  // (reconnect) never doubles.
  addInlineTip(tip) {
    if (!tip || tip.id == null) return;
    if (this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(tip.id))}"]`)) return;

    const pinned = this.isPinnedToBottom();
    this.messagesTarget.appendChild(this.buildTipMessage(tip));
    if (pinned) this.scrollToBottom();
  }

  // Clones tipMessageTemplate and fills its slots; mirrors buildMessage plus the amount badge.
  buildTipMessage(tip) {
    const wrapper = this.tipMessageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.tipId = tip.id;
    if (tip.user) wrapper.dataset.userId = tip.user.id;

    this.fillAvatar(wrapper, tip.user);
    this.fillIdentity(wrapper, (tip.user && tip.user.name) || "", !!(tip.user && tip.user.is_channel_admin), tip.user && tip.user.id, !!(tip.user && tip.user.has_subscriber_profile));
    wrapper.querySelector("[data-chat-tip-amount]").textContent = this.tipAmountLabel(tip);

    const bodyEl = wrapper.querySelector("[data-chat-message-body]");
    const body = (tip.body || "").trim();
    if (body !== "") {
      bodyEl.textContent = body;
    } else {
      bodyEl.remove();
    }

    if (this.moderatorValue) wrapper.appendChild(this.buildTipModeratorActions(tip));

    return wrapper;
  }

  // A hover-revealed remove control for the inline tip, mirroring buildModeratorActions with a
  // single action. Reuses the trash icon <template>; the toolbar shell is cloned from
  // tipModeratorActionsTemplate.
  buildTipModeratorActions(tip) {
    const actions = this.tipModeratorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-remove-tip-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelTipIdParam = tip.id;

    return actions;
  }

  removeInlineTip(id) {
    if (id == null) return;
    const node = this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(id))}"]`);
    if (node) node.remove();
  }

  // A message delivered to this recipient alone (see LiveChat::PrivateMessage) - currently only
  // the timeout notice. Never buffered/backfilled, so unlike a room message it carries no dedupe
  // concern and needs no data-client-msg-id: it can only ever arrive once, live.
  appendPrivateMessage(message) {
    const pinned = this.isPinnedToBottom();
    this.messagesTarget.appendChild(this.buildPrivateMessage(message));
    if (pinned) this.scrollToBottom();
  }

  // Clones privateMessageTemplate and fills it via the same fillAvatar/fillIdentity/fillBody
  // helpers buildMessage uses - `user` is null only for a future system-authored message (not
  // reachable by anything that sends today), so every branch below falls back the same way
  // buildTipMessage already does for a possibly-null tip.user.
  buildPrivateMessage(message) {
    const wrapper = this.privateMessageTemplateEl.content.cloneNode(true).firstElementChild;

    this.fillAvatar(wrapper, message.user);
    this.fillIdentity(
      wrapper,
      (message.user && message.user.name) || "",
      !!(message.user && message.user.is_channel_admin),
      message.user && message.user.id,
      !!(message.user && message.user.has_subscriber_profile)
    );
    this.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!(message.user && message.user.is_moderator));

    return wrapper;
  }

  // A moderator removed (shadow-banned) this user from the room (see LiveStreamChannel#ban_user).
  // The frame is broadcast room-wide, so it reaches the removed user's own browser too - but the
  // whole point of a shadow ban is that their own view never changes, so the self-case returns
  // before touching the DOM at all. Reversing this ordering would have a banned user watch their
  // own message history purge itself: a total self-reveal.
  handleRemoveUser(userId) {
    if (String(userId) === String(this.currentUserIdValue)) return;

    this.messagesTarget
      .querySelectorAll(`[data-user-id="${CSS.escape(String(userId))}"]`)
      .forEach((node) => node.remove());
  }

  // A moderator-applied timeout - unlike handleRemoveUser, which removes the target's rows from
  // everyone else's view, a timeout leaves every existing message exactly where it is (nothing is
  // removed from the DOM); only the composer is disabled, with a countdown that re-enables it once
  // the TTL this echoes reaches zero. seconds is always "time remaining from now," never a stored
  // total (see docs/livestream-chat.md), so a reconnect mid-timeout - which re-sends this from
  // `subscribed` with the live TTL at that instant - simply restarts the countdown from the fresh
  // value; there is nothing to diff it against.
  handleTimedOut(secondsRemaining) {
    const seconds = Number(secondsRemaining) || 0;
    if (seconds <= 0) return;

    this.timedOut = true;
    this.setInputDisabled(true);
    // Reuses the same halt used for a disconnect: no automatic resend could land on a message
    // sent right as the mute landed, so it's surfaced as failed with its usual retry affordance.
    this.haltAllPending();
    if (this.hasTimedOutTarget) this.timedOutTarget.classList.remove("hidden");

    this.timeoutDeadlineIso = new Date(Date.now() + seconds * 1000).toISOString();
    if (this.timeoutTimer) window.clearInterval(this.timeoutTimer);
    this.tickTimeoutCountdown();
    this.timeoutTimer = window.setInterval(() => this.tickTimeoutCountdown(), 1000);
  }

  // Reuses utils/live_poll's countdown() (built for the poll deadline countdown) against a local
  // deadline derived once in handleTimedOut, rather than duplicating the remaining/label math -
  // there is no server-provided deadline to parse directly, only a live "seconds left" snapshot.
  tickTimeoutCountdown() {
    const result = countdown(this.timeoutDeadlineIso);
    const remaining = result ? result.remaining : 0;

    if (this.hasTimedOutCountdownTarget) this.timedOutCountdownTarget.textContent = result ? result.label : "0:00";
    if (remaining > 0) return;

    if (this.timeoutTimer) window.clearInterval(this.timeoutTimer);
    this.timeoutTimer = null;
    this.timedOut = false;
    if (this.hasTimedOutTarget) this.timedOutTarget.classList.add("hidden");
    this.enableInput();
  }

  handleSlowMode() {
    this.enableInput();
    this.showHint("Slow mode is on — please wait a moment before sending another message.");
  }

  // Shows a message under the composer for 4s, clearing any hint already pending so a fast
  // sequence of calls doesn't hide one early. Shared by handleSlowMode and send()'s no-subscription
  // guard; does not touch enableInput - a hint shown while the composer stays disabled must not
  // re-enable it.
  showHint(message) {
    this.hintTarget.textContent = message;
    this.hintTarget.classList.remove("hidden");

    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hintTarget.classList.add("hidden"), 4000);
  }

  enableInput() {
    if (this.closed || this.timedOut) return;
    this.setInputDisabled(false);
  }

  setInputDisabled(disabled) {
    this.inputTarget.disabled = disabled;
    this.submitTarget.disabled = disabled;
  }

  isPinnedToBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  newClientMsgId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
